// ✅ 모바일 전용 IncidentEditor - 웹사이트 코드에 영향 없음
import React, { useEffect, useMemo, useState, useRef } from "react";
import TinyMCEeditor from "../../const/TinyMCEeditor";
import MobileAttachmentBox from "../../common/papers/MobileAttachmentBox";
import MobileReferenceModal from "../../approval/MobileReferenceModal";

export default function MobileIncidentEditor({
  initial = {},
  onChange,
  readOnly = false,
  currentUser = {},
}) {
  const attachmentRef = useRef(null);
  const editorRef = useRef(null);
  const today = new Date().toISOString().slice(0, 10);

  const me = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const meId = me?.id || null;

  // 상단 공통
  const [form, setForm] = useState({
    doc_date: initial.doc_date || new Date().toISOString().slice(0, 10),
    title: initial.title || "",
    note: initial.note || "",
    status: initial.status || "none",
  });

  // 사고 메타 (모델 1:1)
  const [meta, setMeta] = useState({
    incident_no: initial.incident_no || "",
    incident_date:
      initial.incident_date || new Date().toISOString().slice(0, 10),
    location: initial.location || "",
    incident_type: initial.incident_type || "",
    severity: initial.severity || "MODERATE",
    severity_note: initial.severity_note || "",
    reporter: initial.reporter || "",
    witnesses: Array.isArray(initial.witnesses) ? initial.witnesses : [],
    minor_injury_count: Number(initial.minor_injury_count ?? 0),
    major_injury_count: Number(initial.major_injury_count ?? 0),
    fatality_count: Number(initial.fatality_count ?? 0),
    property_damage_cost: Number(initial.property_damage_cost ?? 0),
    property_damage_note: initial.property_damage_note || "",
    description: initial.description || "",
    cause: initial.cause || "",
    corrective_action: initial.corrective_action || "",
    preventive_action: initial.preventive_action || "",
  });

  // 열람가능자 설정
  const [referenceUserIds, setReferenceUserIds] = useState([]);
  const [referenceUserDetails, setReferenceUserDetails] = useState([]);
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);

  const openReferenceModal = () => setIsReferenceModalOpen(true);
  const closeReferenceModal = () => setIsReferenceModalOpen(false);

  const handleReferenceChange = (ids) => setReferenceUserIds(ids);

  const handleReferenceSave = (ids, usersForFront = []) => {
    setReferenceUserIds(ids);
    setReferenceUserDetails(
      (usersForFront || []).filter((u) => u && u.id !== meId)
    );
    return true;
  };

  // 사진 메타(업로드 워크플로 별도)
  const [photos] = useState(initial.photos || []);

  // helpers
  const onForm = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onMeta = (k, v) => setMeta((p) => ({ ...p, [k]: v }));

  const addWitness = () =>
    onMeta("witnesses", [
      ...(meta.witnesses || []),
      { id: crypto.randomUUID(), name: "" },
    ]);

  const updateWitness = (id, name) =>
    onMeta(
      "witnesses",
      (meta.witnesses || []).map((w, i) => {
        const key = w?.id ?? i;
        return key === id ? (typeof w === "string" ? name : { ...w, name }) : w;
      })
    );

  const removeWitness = (id) =>
    onMeta(
      "witnesses",
      (meta.witnesses || []).filter((w, i) => (w?.id ?? i) !== id)
    );

  // 자동 제목
  const autoTitle = useMemo(() => {
    const t = meta.incident_type?.trim();
    const d = meta.incident_date;
    const l = meta.location?.trim();
    if (t && l) return `[사고] ${t} - ${l} (${d})`;
    if (t) return `[사고] ${t} (${d})`;
    if (l) return `[사고] ${l} (${d})`;
    return "";
  }, [meta.incident_type, meta.location, meta.incident_date]);

  useEffect(() => {
    if (!Array.isArray(initial.reference_users)) return;

    const myId = Number(meId);

    const filtered = initial.reference_users.filter(
      (id) => Number(id) !== myId
    );

    setReferenceUserIds(filtered);

    setReferenceUserDetails(
      (initial.reference_user_details || []).filter((u) => u.id !== myId)
    );
  }, [initial, meId]);

  // 부모 전달
  useEffect(() => {
    if (!onChange) return;
    const finalTitle = form.title?.trim() || autoTitle?.trim();

    const refUsers = [...referenceUserIds];
    if (meId) {
      refUsers.push(meId);
    }

    onChange({
      dns: initial.dns || "",
      doc_type: "incident",
      title: finalTitle,
      status: form.status,
      attachmentRef,
      meta: {
        ...meta,
        doc_date: form.doc_date || today,
        note: form.note,
        photos,
        reference_users: refUsers,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, meta, photos, autoTitle, referenceUserIds, meId, onChange, initial.dns, today]);

  const editing = !readOnly;

  const witnessList = (
    <div className="space-y-2 min-w-0">
      {(meta.witnesses || []).map((w, i) => {
        const id = w?.id ?? i;
        const val = typeof w === "string" ? w : w?.name || "";
        return (
          <div
            key={id}
            className="flex items-center gap-2 min-w-0"
          >
            <input
              className="flex-1 rounded border border-gray-200 px-3 py-2 text-sm min-w-0 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={val}
              onChange={(e) => updateWitness(id, e.target.value)}
              disabled={readOnly}
              placeholder="이름(필요 시 연락처/소속)"
            />
            {editing && (
              <button
                type="button"
                className="btn-delete-sm flex-shrink-0 whitespace-nowrap"
                onClick={() => removeWitness(id)}
              >
                삭제
              </button>
            )}
          </div>
        );
      })}
      {editing && (
        <button
          type="button"
          className="btn-create text-sm px-4 py-2 w-full"
          onClick={addWitness}
        >
          목격자 추가
        </button>
      )}
      <p className="text-xs text-gray-500">
        목격자 진술은 가능한 한 구체적으로 기록하세요. (이름/연락처)
      </p>
    </div>
  );

  return (
    <div className="space-y-4 min-w-0 overflow-x-hidden">
      {/* ------------------------------ */}
      {/* # 1. 문서 정보 */}
      {/* ------------------------------ */}
      <div className="space-y-3">
        {/* 제목 */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-700 block">
            제목
          </label>
          <input
            type="text"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={autoTitle || "문서 제목"}
            value={form.title}
            onChange={(e) => onForm("title", e.target.value)}
            disabled={readOnly}
          />
          <p className="text-xs text-gray-500">
            미입력 시 자동 제목: {autoTitle}
          </p>
        </div>

        {/* 작성일 + 작성자 */}
        <div className="grid grid-cols-1 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              작성일
            </label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-50"
              value={today}
              disabled
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              작성자
            </label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-50"
              value={me?.name || ""}
              disabled
            />
          </div>
        </div>

        {/* 비고 */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-700 block">
            비고
          </label>
          <div className="min-w-0 w-full">
            <TinyMCEeditor
              initialValue={form.note || ""}
              editorRef={editorRef}
              onChange={(v) => onForm("note", v)}
            />
          </div>
        </div>
      </div>

      {/* ------------------------------ */}
      {/* # 2. 사고 상세 정보 */}
      {/* ------------------------------ */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">사고 상세 정보</h3>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              사고번호
            </label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={meta.incident_no}
              onChange={(e) => onMeta("incident_no", e.target.value)}
              disabled={readOnly}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              사고일자
            </label>
            <input
              type="date"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={meta.incident_date}
              onChange={(e) => onMeta("incident_date", e.target.value)}
              disabled={readOnly}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              장소
            </label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={meta.location}
              onChange={(e) => onMeta("location", e.target.value)}
              disabled={readOnly}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              사고유형
            </label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={meta.incident_type}
              onChange={(e) => onMeta("incident_type", e.target.value)}
              disabled={readOnly}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              중대도
            </label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={meta.severity}
              onChange={(e) => onMeta("severity", e.target.value)}
              disabled={readOnly}
            >
              <option value="LOW">LOW</option>
              <option value="MODERATE">MODERATE</option>
              <option value="HIGH">HIGH</option>
              <option value="CRITICAL">CRITICAL</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              중대도 메모
            </label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white min-h-[70px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={meta.severity_note}
              onChange={(e) => onMeta("severity_note", e.target.value)}
              placeholder="중대도 판단 근거 또는 참고사항"
              disabled={readOnly}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              신고자*
            </label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={meta.reporter}
              onChange={(e) => onMeta("reporter", e.target.value)}
              disabled={readOnly}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              목격자
            </label>
            {witnessList}
          </div>
        </div>
      </div>

      {/* ------------------------------ */}
      {/* # 3. 피해 / 비용 */}
      {/* ------------------------------ */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">피해 / 비용</h3>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              경상자 수
            </label>
            <input
              type="number"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              min={0}
              value={meta.minor_injury_count}
              onChange={(e) =>
                onMeta("minor_injury_count", Number(e.target.value || 0))
              }
              disabled={readOnly}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              중상자 수
            </label>
            <input
              type="number"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              min={0}
              value={meta.major_injury_count}
              onChange={(e) =>
                onMeta("major_injury_count", Number(e.target.value || 0))
              }
              disabled={readOnly}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              사망자 수
            </label>
            <input
              type="number"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              min={0}
              value={meta.fatality_count}
              onChange={(e) =>
                onMeta("fatality_count", Number(e.target.value || 0))
              }
              disabled={readOnly}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              재산 피해액(원)
            </label>
            <input
              type="number"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={meta.property_damage_cost}
              onChange={(e) =>
                onMeta(
                  "property_damage_cost",
                  Number(e.target.value || 0)
                )
              }
              placeholder="숫자만 입력"
              disabled={readOnly}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              재산 피해 메모
            </label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={meta.property_damage_note}
              onChange={(e) =>
                onMeta("property_damage_note", e.target.value)
              }
              disabled={readOnly}
            />
          </div>

          <p className="text-xs text-gray-500">
            총 인명피해:{" "}
            {meta.minor_injury_count +
              meta.major_injury_count +
              meta.fatality_count}
            명
          </p>
        </div>
      </div>

      {/* ------------------------------ */}
      {/* # 4. 사고 설명 / 원인 / 조치 */}
      {/* ------------------------------ */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">사고 설명 / 원인 / 조치</h3>

        <div className="space-y-3">
          {/* 사고 개요 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              사고 개요*
            </label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white min-h-[120px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={meta.description}
              onChange={(e) => onMeta("description", e.target.value)}
              placeholder="무엇이, 언제, 어디서, 누구에게, 어떻게 발생했는지 상세히"
              disabled={readOnly}
            />
          </div>

          {/* 원인 분석 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              원인 분석
            </label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={meta.cause}
              onChange={(e) => onMeta("cause", e.target.value)}
              disabled={readOnly}
            />
          </div>

          {/* 즉시 시정조치 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              즉시 시정조치
            </label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={meta.corrective_action}
              onChange={(e) =>
                onMeta("corrective_action", e.target.value)
              }
              disabled={readOnly}
            />
          </div>

          {/* 재발 방지대책 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 block">
              재발 방지대책
            </label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={meta.preventive_action}
              onChange={(e) =>
                onMeta("preventive_action", e.target.value)
              }
              disabled={readOnly}
            />
          </div>
        </div>
      </div>

      {/* ------------------------------ */}
      {/* # 5. 열람 권한 설정 */}
      {/* ------------------------------ */}
      <div className="space-y-3 border-t border-gray-200 pt-4">
        <h3 className="text-sm font-semibold text-gray-900">열람 권한</h3>

        {!readOnly && (
          <>
            <button
              type="button"
              className="btn-basic text-sm px-4 py-2 w-full"
              onClick={openReferenceModal}
            >
              열람 사용자 선택
            </button>

            {referenceUserDetails.length > 0 && (
              <div className="flex flex-wrap gap-2 text-sm">
                {referenceUserDetails.map((u) => (
                  <span
                    key={u.id}
                    className="px-2 py-1 rounded-lg bg-gray-100 border border-gray-200 text-xs flex items-center gap-1"
                  >
                    {u.last_name}
                    {u.first_name}
                    {u.position && (
                      <span className="text-gray-500">
                        {u.position}
                      </span>
                    )}
                    <span className="text-gray-500">({u.company})</span>
                  </span>
                ))}
              </div>
            )}
          </>
        )}

        {readOnly && referenceUserDetails?.length > 0 && (
          <div className="flex flex-wrap gap-2 text-sm text-gray-700">
            {referenceUserDetails.map((u) => (
              <span
                key={u.id}
                className="px-2 py-1 rounded-lg bg-gray-100 border border-gray-200 text-xs"
              >
                {u.last_name}
                {u.first_name} ({u.position}, {u.company})
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 첨부파일 */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-gray-700 block">
          첨부파일
        </label>
        <MobileAttachmentBox ref={attachmentRef} />
      </div>

      {/* modal */}
      <MobileReferenceModal
        isOpen={isReferenceModalOpen}
        onClose={closeReferenceModal}
        selectedIds={referenceUserIds}
        onChange={handleReferenceChange}
        onSave={handleReferenceSave}
        name="열람 가능 사용자"
      />
    </div>
  );
}

